import { BareSimpleSailing, BareSimpleSailingDay } from '@go-shared/types/sailing';
import {
  TemplateFragmentType,
  StrategicLinkFragment,
  TemplateFragmentUnlinkStrategy,
  FRAGMENT_TYPES,
} from '@go-shared/types/templateFragment';
import { Dropdown, DropdownItem } from '@spark-labs/avocado-ui';
import React, { useState } from 'react';

import { FragmentBlockSync } from './TemplateLinkForm';
import { BubbleButton, BubbleOverlay } from '../BubbleUI';
import { SimpleTemplate } from '@go-shared/types/template';
import { User } from '@go-shared/types/user';
import { getDayFriendlyName } from '@go-shared/lib/sailing';
import { FormGenericContainer } from '../FormGenericContainer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { canLinkTemplates } from '@go-shared/lib/permissions';

interface DaySummaryPageProps {
  user: User;
  sailing: BareSimpleSailing;
  sailingDay: BareSimpleSailingDay;
  templates: SimpleTemplate[];
  onUnlinkTemplateFragments?: (linkFragments: StrategicLinkFragment[]) => Promise<void>;
  onGoNext?: () => void;
  onTemplateNameClick?: (blockSync: FragmentBlockSync) => void;
  onSailingDayNameClick?: (sailing: BareSimpleSailing, sailingDay: BareSimpleSailingDay) => void;
  onCreateTemplateClick?: (sailing: BareSimpleSailing, sailingDay: BareSimpleSailingDay) => void;
  onClose?: () => void;
}

interface DaySummaryPageFragmentBlock {
  key: string;
  type: TemplateFragmentType;
  name: string;
  classes: string;
  color: string;
  linked?: FragmentBlockSync;
}

function generateFragmentBlocks(): DaySummaryPageFragmentBlock[] {
  return [
    {
      key: `block-events`,
      type: 'events',
      name: 'Events',
      classes: 'text-white',
      color: 'bg-lime',
      linked: undefined,
    },
    {
      key: `block-venues`,
      type: 'venueData',
      name: 'Venue Data',
      classes: 'text-white',
      color: 'bg-banana',
      linked: undefined,
    },
    {
      key: `block-content`,
      type: 'content',
      name: 'Content',
      classes: 'text-white',
      color: 'bg-ocean',
      linked: undefined,
    },
    {
      key: `block-feed`,
      type: 'feed',
      name: 'Print Program',
      classes: 'text-white',
      color: 'bg-grape',
      linked: undefined,
    },
  ];
}

export default function DaySummaryPage(props: DaySummaryPageProps) {
  const templateFragments = props.sailingDay.templateFragments || {};
  const templateHistory = props.sailingDay.templateHistory || {};
  const [isBusy, setIsBusy] = useState(false);

  const fragmentBlocks = generateFragmentBlocks();

  for (const block of fragmentBlocks) {
    const templateId = templateFragments[block.type];
    const historyId = templateHistory[block.type];

    const idToUse = templateId ? templateId : historyId;

    if (!idToUse) continue;

    const template = props.templates.find(t => t._id === idToUse);
    if (!template) continue;

    block.linked = {
      type: block.type,
      text: template.name,
      templateId: idToUse,
      active: !!templateId,
    };
  }

  async function handleOnSelectUnlinkDropdown(
    itm: DropdownItem,
    block: DaySummaryPageFragmentBlock
  ) {
    try {
      setIsBusy(true);

      switch (itm.key) {
        case 'partial':
        case 'full':
          if (!props.onUnlinkTemplateFragments || !block.linked) return;

          await props.onUnlinkTemplateFragments([
            {
              sailingId: props.sailing._id,
              sailingDayId: props.sailingDay._id,
              templateId: block.linked.templateId,
              unlinkStrategy: itm.key,
              fragmentType: block.type,
            },
          ]);

          break;
      }
    } finally {
      setIsBusy(false);
    }
  }

  async function handleOnSelectUnlinkAllDropdown(
    itm: DropdownItem,
    blocks: DaySummaryPageFragmentBlock[]
  ) {
    const linkedBlocks = blocks.filter(b => b.linked);

    if (linkedBlocks.length === 0) return;

    const templateId = linkedBlocks[0].linked!.templateId;

    try {
      setIsBusy(true);

      switch (itm.key) {
        case 'partial':
        case 'full':
          if (!props.onUnlinkTemplateFragments) return;
          await props.onUnlinkTemplateFragments(
            FRAGMENT_TYPES.map(fragmentType => ({
              sailingId: props.sailing._id,
              sailingDayId: props.sailingDay._id,
              templateId,
              unlinkStrategy: itm.key as TemplateFragmentUnlinkStrategy,
              fragmentType,
            }))
          );

          break;
      }
    } finally {
      setIsBusy(false);
    }
  }

  const userCanEdit = canLinkTemplates(props.user);

  return (
    <div className="tflf-3">
      <FormGenericContainer
        footer={() =>
          userCanEdit ? (
            <>
              {!props.sailingDay.locked && (
                <BubbleButton variant="navy" onClick={props.onGoNext} className="mr-3">
                  Link Template Fragments
                </BubbleButton>
              )}
              <BubbleButton
                variant="navy"
                className="-ml-3"
                inverse
                onClick={() =>
                  props.onCreateTemplateClick
                    ? props.onCreateTemplateClick(props.sailing, props.sailingDay)
                    : undefined
                }
              >
                Create Template From Day
              </BubbleButton>
              {!props.sailingDay.locked && (
                <div className="dropdown-fix ml-auto">
                  <Dropdown
                    items={[
                      { key: 'partial', element: 'Unlink Only' },
                      { key: 'full', element: 'Unlink & Clear' },
                    ]}
                    onSelect={itm => handleOnSelectUnlinkAllDropdown(itm, fragmentBlocks)}
                  >
                    {({ toggleRef, ...rest }) => (
                      <BubbleButton
                        inverse
                        buttonRef={toggleRef}
                        {...rest}
                        variant="crimson"
                        iconSuffix="caret-down"
                      >
                        Unlink All Fragments
                      </BubbleButton>
                    )}
                  </Dropdown>
                </div>
              )}
            </>
          ) : (
            <></>
          )
        }
      >
        <div className="flex items-start mb-5">
          <div>
            <div className="text-lg text-slate-dark font-semibold">
              {props.onSailingDayNameClick ? (
                <BubbleButton
                  variant="slate"
                  className="p-0-i text-left"
                  inverse
                  onClick={() =>
                    props.onSailingDayNameClick
                      ? props.onSailingDayNameClick(props.sailing, props.sailingDay)
                      : undefined
                  }
                  icon={['fass', 'chart-tree-map']}
                >
                  <span className="underline">
                    {getDayFriendlyName(props.sailing, props.sailingDay)}
                  </span>
                </BubbleButton>
              ) : (
                <span>{getDayFriendlyName(props.sailing, props.sailingDay)}</span>
              )}
            </div>

            <h2 className="text-2xl text-navy">Template Fragments</h2>
          </div>
          <BubbleButton
            onClick={props.onClose}
            className="ml-auto"
            variant="navy"
            inverse
            icon="xmark-large"
            iconSize="xl"
          />
        </div>
        <div className="-mb-3">
          <BubbleOverlay loading={isBusy}>
            {fragmentBlocks.map(block => (
              <div key={block.key} className="fragment-block mb-3">
                <div
                  className={[
                    'fb-header relative',
                    block.classes,
                    !block.linked
                      ? 'bg-smoke'
                      : !block.linked.active
                      ? 'bg-dirt-lightest'
                      : block.color,
                  ].join(' ')}
                >
                  <div className="flex items-center px-5 py-3">
                    <div className="text-md font-bold mr-auto">{block.name}</div>
                    {props.sailingDay.locked ? (
                      <FontAwesomeIcon icon="lock" />
                    ) : block.linked ? (
                      <FontAwesomeIcon icon={block.linked.active ? 'chain' : 'chain-broken'} />
                    ) : (
                      <div className="text-white italic font-semibold">
                        Not linked to any template
                      </div>
                    )}
                  </div>
                </div>

                {block.linked && (
                  <>
                    {block.linked.active ? (
                      <div className="fb-content p-5 relative">
                        <div className="flex items-center">
                          <div className="flex-grow">
                            <div>
                              <div className="text-md text-slate-dark mb-1">
                                Currently linked to:
                              </div>
                              <BubbleButton
                                variant="velvet"
                                inverse
                                className="text-lg p-0-i whitespace-normal pr-3 text-left"
                                onClick={() =>
                                  props.onTemplateNameClick && block.linked
                                    ? props.onTemplateNameClick(block.linked)
                                    : undefined
                                }
                                icon={[
                                  'fass',
                                  block.type === 'feed' ? 'newspaper' : 'chart-tree-map',
                                ]}
                              >
                                <span className="underline">{block.linked.text}</span>
                              </BubbleButton>
                            </div>
                          </div>
                          {canLinkTemplates(props.user) && (
                            <div className="dropdown-fix ml-auto">
                              <Dropdown
                                items={[
                                  { key: 'partial', element: 'Unlink Only' },
                                  { key: 'full', element: 'Unlink & Clear' },
                                ]}
                                onSelect={itm => handleOnSelectUnlinkDropdown(itm, block)}
                              >
                                {({ toggleRef, ...rest }) => (
                                  <BubbleButton
                                    buttonRef={toggleRef}
                                    {...rest}
                                    variant="crimson"
                                    iconSuffix="caret-down"
                                  >
                                    Unlink Fragment
                                  </BubbleButton>
                                )}
                              </Dropdown>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="p-5">
                        <div className="text-md text-slate-dark mb-1">Last sourced from:</div>
                        <BubbleButton
                          key={block.linked.templateId}
                          variant="velvet"
                          inverse
                          className="text-lg p-0-i"
                          onClick={() =>
                            props.onTemplateNameClick && block.linked
                              ? props.onTemplateNameClick(block.linked)
                              : undefined
                          }
                          icon={['fass', block.type === 'feed' ? 'newspaper' : 'chart-tree-map']}
                        >
                          <span className="underline">{block.linked.text}</span>
                        </BubbleButton>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </BubbleOverlay>
        </div>
      </FormGenericContainer>
    </div>
  );
}
